import {
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeftBar, IconTool } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createOrganizationSchema,
} from "../../seo/utils";

type ToolPlaceholderPageProps = {
  title: string;
  description: string;
  path: string;
  category?: string;
  relatedPath?: string;
  relatedLabel?: string;
};

const TOOLS_PATH = "/tools";

function ToolPlaceholderPage({
  title,
  description,
  path,
  category = "Student Tool",
  relatedPath = TOOLS_PATH,
  relatedLabel = "View All Tools",
}: ToolPlaceholderPageProps) {
  const navigate = useNavigate();

  return (
    <>
      <Seo
        title={`${title} | SubjectBuddy`}
        description={description}
        path={path}
        noIndex
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: title, path },
          ]),
        ]}
      />

      <Box
        style={{
          background:
            "linear-gradient(135deg, rgba(241, 248, 242, 0.96) 0%, #ffffff 58%, rgba(238, 246, 255, 0.95) 100%)",
          borderBottom: "1px solid #e5efe7",
        }}
      >
        <Container size="xl" py={64}>
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>{title}</Text>
            </Breadcrumbs>

            <Button
              color="green"
              variant="subtle"
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate(TOOLS_PATH)}
              style={{ width: "fit-content", paddingLeft: 0 }}
            >
              Back to Tools
            </Button>

            <Paper withBorder radius="xl" p="xl">
              <Stack gap="md" maw={720}>
                <Group gap="sm">
                  <Badge color="green" size="lg" radius="sm">
                    {category}
                  </Badge>
                  <Badge color="blue" size="lg" radius="sm" variant="light">
                    Placeholder Page
                  </Badge>
                </Group>
                <Group gap="sm">
                  <IconTool size={28} color="#2b8a3e" />
                  <Title order={1}>{title}</Title>
                </Group>
                <Text c="dimmed" size="lg" style={{ lineHeight: 1.8 }}>
                  {description}
                </Text>
                <Text c="dimmed">
                  This route is available as a crawlable placeholder so users
                  can browse related tools from the main generator and tools
                  index without hitting a dead link.
                </Text>
                <Group gap="md">
                  <Button component={Link} to={relatedPath} color="green">
                    {relatedLabel}
                  </Button>
                  <Button component={Link} to={TOOLS_PATH} variant="light" color="blue">
                    Open Tools Hub
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </>
  );
}

export default ToolPlaceholderPage;
